import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProfile, updateProfile } from "../../rtk/thunks/authThunk";
import "./AccountSettings.css";
import countries from "../../utils/countries";

const countryOptions = ["", ...countries];

const emptyForm = { firstName: "", lastName: "", email: "", country: "" };

const toForm = (user) => ({
  firstName: user?.first_name || "",
  lastName: user?.last_name || "",
  email: user?.email || "",
  country: user?.country || "",
});

const formatError = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.filter(Boolean).join(" ");
  return (
    value.detail ||
    value.message ||
    Object.values(value)
      .flat()
      .find((item) => typeof item === "string" && item.trim()) ||
    "Failed to update profile."
  );
};

export default function ProfileSettings() {
  const dispatch = useDispatch();
  const { user, access_token } = useSelector((state) => state.auth);
  const accessToken = access_token || localStorage.getItem("access_token");

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [isProfileUpdated, setIsProfileUpdated] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!accessToken) return;
    if (!user) {
      dispatch(getProfile());
    }
  }, [accessToken, dispatch, user]);

  useEffect(() => {
    if (user) {
      setForm(toForm(user));
    }
  }, [user]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        country: form.country,
      };
      await dispatch(updateProfile(payload)).unwrap();

      setIsProfileUpdated(true);
      setTimeout(() => {
        setIsProfileUpdated(false);
      }, 2000)
      setTimeout(() => {
        window.location = '/user-profile'; // redirect to profile page after update
      }, 3000); // hide success message after 3 seconds
    } catch (err) {
      setSubmitError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancel() {
    setForm(toForm(user));
    setSubmitError(null);
  }

  return (
    <div className="profile-settings-bg">
      <div className="profile-breadcrumb">
        <span>Home</span>
        <span className="profile-breadcrumb-chevron">{'>'}</span>
        <span className="profile-breadcrumb-current">Settings</span>
      </div>
      <div className="profile-settings-card">
        <h2 className="profile-settings-title">Profile Settings</h2>
        <form className="profile-form" onSubmit={handleSubmit}>
          <div className="profile-section-label">Personal Details</div>
          <div className="profile-row">
            <input
              type="text"
              name="firstName"
              placeholder="First name"
              value={form.firstName}
              onChange={handleChange}
              className="profile-input"
              autoComplete="given-name"
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last name"
              value={form.lastName}
              onChange={handleChange}
              className="profile-input"
              autoComplete="family-name"
            />
          </div>
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            className="profile-input"
            autoComplete="email"
          />
          <select
            name="country"
            value={form.country}
            onChange={handleChange}
            className="profile-input"
          >
            {countryOptions.map((country, idx) => (
              <option key={idx} value={country}>
                {country === "" ? "Country" : country}
              </option>
            ))}
          </select>
          {
              isProfileUpdated && <div className="profile-update-success " style={{color : 'green'}}>Profile updated successfully!</div>
          }
          {
              submitError && <div className="profile-update-error" style={{color: 'crimson'}}>{submitError}</div>
          }
          {/* Change password moved to standalone page */}
          <div className="profile-footer">
            <button
              type="button"
              className="profile-cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="profile-save-btn"
              disabled={submitting}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
